import { create } from 'zustand'
import type { SerializedEvent } from '../matrix/events'

/** Lightweight per-thread metadata shown in the global Threads list. */
export interface ThreadInfo {
  roomId: string
  /** Event id of the thread root. */
  rootId: string
  /** Root message preview (body), for the threads list. */
  rootPreview: string
  /** Sender of the root message. */
  rootSender: string
  /** Number of replies (excludes the root). */
  replyCount: number
  /** Timestamp of the latest reply (or the root if none yet). */
  latestTs: number
  /** Sender of the latest reply. */
  latestSender: string
  /** Set of userIds who have replied. */
  participants: string[]
}

interface ThreadState {
  /** Per-room thread metadata, keyed by rootId. */
  threads: Map<string, Map<string, ThreadInfo>>
  /** Loaded replies for a thread, keyed by rootId (populated when a panel opens). */
  threadEvents: Map<string, SerializedEvent[]>
  /**
   * Last-read reply timestamp per thread, keyed by rootId. A thread is unread
   * when its latestTs is newer than this. Tracked independently of room unread
   * state — scrolling past the root in the main timeline is irrelevant.
   */
  lastReadTs: Map<string, number>

  /**
   * Record a thread reply: updates the thread's metadata and appends to its
   * loaded events. Called from the sync layer for every event carrying a
   * `threadRootId`. `isOwn` marks our own echoes so they don't flag as unread.
   */
  addThreadReply: (event: SerializedEvent, isOwn: boolean) => void
  /**
   * Seed (or update) a thread's root metadata from a thread-root event, so the
   * threads list shows a preview even before replies stream in. Idempotent.
   */
  seedThreadRoot: (root: SerializedEvent) => void
  /** Replace the loaded reply set for a thread (e.g. after paginating). */
  setThreadEvents: (rootId: string, events: SerializedEvent[]) => void
  /** Mark a thread read up to the given timestamp (its latest reply on open). */
  markThreadRead: (rootId: string, ts: number) => void
  /** Remove an event (local-echo replacement / redaction). */
  removeThreadEvent: (rootId: string, eventId: string) => void
}

/** Shared empty array so selectThreadEvents returns a stable reference for
 * threads with no loaded replies (a fresh `[]` each call triggers React #185
 * under zustand v5's reference equality). */
const EMPTY_EVENTS: SerializedEvent[] = []

function insertSorted(events: SerializedEvent[], event: SerializedEvent): SerializedEvent[] {
  const existingIdx = events.findIndex((e) => e.eventId === event.eventId)
  if (existingIdx !== -1) {
    const copy = [...events]
    copy[existingIdx] = event
    return copy
  }
  const insertIdx = events.findIndex((e) => e.timestamp > event.timestamp)
  const copy = [...events]
  if (insertIdx === -1) copy.push(event)
  else copy.splice(insertIdx, 0, event)
  return copy
}

export const useThreadStore = create<ThreadState>((set) => ({
  threads: new Map(),
  threadEvents: new Map(),
  lastReadTs: new Map(),

  addThreadReply: (event, isOwn) =>
    set((state) => {
      const rootId = event.threadRootId
      if (!rootId) return state

      // Append to loaded events.
      const threadEvents = new Map(state.threadEvents)
      threadEvents.set(rootId, insertSorted(threadEvents.get(rootId) ?? [], event))

      // Update per-room metadata.
      const threads = new Map(state.threads)
      const roomThreads = new Map(threads.get(event.roomId) ?? [])
      const prev = roomThreads.get(rootId)
      const replies = threadEvents.get(rootId) ?? []
      const participants = new Set(prev?.participants ?? [])
      participants.add(event.sender)
      roomThreads.set(rootId, {
        roomId: event.roomId,
        rootId,
        rootPreview: prev?.rootPreview ?? '',
        rootSender: prev?.rootSender ?? '',
        replyCount: replies.length,
        latestTs: Math.max(prev?.latestTs ?? 0, event.timestamp),
        latestSender: event.sender,
        participants: [...participants],
      })
      threads.set(event.roomId, roomThreads)

      // Our own replies are implicitly read.
      const lastReadTs = new Map(state.lastReadTs)
      if (isOwn) lastReadTs.set(rootId, Math.max(lastReadTs.get(rootId) ?? 0, event.timestamp))

      return { threads, threadEvents, lastReadTs }
    }),

  seedThreadRoot: (root) =>
    set((state) => {
      const rootId = root.eventId
      const threads = new Map(state.threads)
      const roomThreads = new Map(threads.get(root.roomId) ?? [])
      const prev = roomThreads.get(rootId)
      const preview = typeof root.content.body === 'string' ? (root.content.body as string) : ''
      roomThreads.set(rootId, {
        roomId: root.roomId,
        rootId,
        rootPreview: preview,
        rootSender: root.sender,
        replyCount: prev?.replyCount ?? root.threadSummary?.count ?? 0,
        latestTs: Math.max(prev?.latestTs ?? 0, root.threadSummary?.latestEventTs ?? root.timestamp),
        latestSender: prev?.latestSender ?? root.sender,
        participants: prev?.participants ?? [],
      })
      threads.set(root.roomId, roomThreads)
      return { threads }
    }),

  setThreadEvents: (rootId, events) =>
    set((state) => {
      const threadEvents = new Map(state.threadEvents)
      threadEvents.set(rootId, [...events].sort((a, b) => a.timestamp - b.timestamp))
      return { threadEvents }
    }),

  markThreadRead: (rootId, ts) =>
    set((state) => {
      const lastReadTs = new Map(state.lastReadTs)
      lastReadTs.set(rootId, Math.max(lastReadTs.get(rootId) ?? 0, ts))
      return { lastReadTs }
    }),

  removeThreadEvent: (rootId, eventId) =>
    set((state) => {
      const threadEvents = new Map(state.threadEvents)
      const events = threadEvents.get(rootId)
      if (!events) return state
      threadEvents.set(rootId, events.filter((e) => e.eventId !== eventId))
      return { threadEvents }
    }),
}))

/** Whether a single thread has unread replies. */
export function threadHasUnread(info: ThreadInfo, lastReadTs: number): boolean {
  return info.latestTs > lastReadTs
}

/**
 * Selector: all threads across all rooms, newest activity first.
 *
 * NOTE: this allocates a fresh array on every call, so consumers MUST wrap it
 * in `useShallow` (or otherwise memoise) — a raw `useThreadStore(selectAllThreads)`
 * returns a new reference each render and drives an infinite re-render loop
 * (React #185).
 */
export function selectAllThreads(s: ThreadState): ThreadInfo[] {
  const all: ThreadInfo[] = []
  for (const roomThreads of s.threads.values()) {
    for (const info of roomThreads.values()) all.push(info)
  }
  return all.sort((a, b) => b.latestTs - a.latestTs)
}

/** Selector: true when any thread anywhere has unread replies (global badge). */
export function selectAnyThreadUnread(s: ThreadState): boolean {
  for (const roomThreads of s.threads.values()) {
    for (const info of roomThreads.values()) {
      if (info.latestTs > (s.lastReadTs.get(info.rootId) ?? 0)) return true
    }
  }
  return false
}

/** Selector: loaded replies for an open thread. */
export function selectThreadEvents(rootId: string) {
  return (s: ThreadState) => s.threadEvents.get(rootId) ?? EMPTY_EVENTS
}
